import { useState } from 'react';
import { Bell, BellOff, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { requestPushPermissionsAndRegister } from '@/services/pushNotifications';

interface PushNotificationSetupProps {
  onComplete?: (result: { enabled: boolean }) => void;
}

/**
 * Controlled dialog component for requesting push notification permissions.
 * 
 * IMPORTANT: This component is purely controlled by PushNotificationsGate.
 * It has NO internal logic for route/auth detection - the Gate handles all of that.
 * When this component mounts, the dialog opens immediately.
 * 
 * The iOS system prompt will ONLY appear when the user clicks "Activar Notificaciones".
 */
export const PushNotificationSetup = ({ onComplete }: PushNotificationSetupProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    
    try {
      // This is the ONLY place where the iOS permission prompt is triggered
      const success = await requestPushPermissionsAndRegister();
      
      if (success) {
        toast({
          title: 'Notificaciones activadas',
          description: 'Recibirás alertas de nuevos pedidos y actualizaciones importantes.',
        });
        localStorage.setItem('push_notification_prompt_seen', 'true');
        onComplete?.({ enabled: true });
      } else {
        toast({
          title: 'No se pudieron activar',
          description: 'Por favor habilita las notificaciones en la configuración de tu dispositivo.',
          variant: 'destructive',
        });
        // NO marcar como visto en fallo técnico - permitir reintento en próximo login
        onComplete?.({ enabled: false });
      }
    } catch (error) {
      console.error('Error activando notificaciones:', error);
      toast({
        title: 'Error',
        description: 'Ocurrió un error al activar las notificaciones.',
        variant: 'destructive',
      });
      // NO marcar como visto en error - permitir reintento en próximo login
      onComplete?.({ enabled: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('push_notification_prompt_seen', 'true');
    onComplete?.({ enabled: false });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleSkip();
    }
  };

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Bell className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-center">Activar Notificaciones</DialogTitle>
          <DialogDescription className="text-center">
            Recibe alertas instantáneas cuando:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100">
              <Smartphone className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium">Nuevos pedidos</p>
              <p className="text-xs text-muted-foreground">
                Clientes envían pedidos desde el portal
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100">
              <Bell className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium">Autorizaciones pendientes</p>
              <p className="text-xs text-muted-foreground">
                Órdenes de compra y cotizaciones por aprobar
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <BellOff className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium">Alertas de inventario</p>
              <p className="text-xs text-muted-foreground">
                Stock bajo y productos por caducar
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button 
            onClick={handleEnableNotifications} 
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? 'Configurando... (puede tomar unos segundos)' : 'Activar Notificaciones'}
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleSkip}
            className="w-full"
          >
            Ahora no
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PushNotificationSetup;
